import React, { useState, useCallback } from 'react'
import { Alert } from 'antd'
import { IconHourGlass, IconCheck, IconClose } from '@edulastic/icons'
import { white } from '@edulastic/colors'
import { segmentApi } from '@edulastic/api'
import {
  fetchIncompleteConversionsCount,
  fetchConversionByIdPear,
  INCOMPLETE_CONVERSIONS_POLL_INITIAL_MS,
  INCOMPLETE_CONVERSIONS_POLL_MAX_MS,
} from '../../utils/bulkConverterPearApi'
import {
  buildBulkConversionCsv,
  downloadCsvFile,
  getBulkConversionFileSummary,
} from '../../utils/bulkConversionCsv'
import { markBulkConversionBannerDismissed } from '../../utils/pdlBulkUploadHandoff'
import { BULK_CONVERTER_SEGMENT } from '../../../constants/bulkConverterSegment'
import {
  BulkConvertStatusBarWrapper,
  BulkConvertStatusBarPanel,
  BulkConvertStatusBarSummaryRow,
  BulkConvertStatusIconWrap,
  BulkConvertStatusMessage,
  BulkConvertStatusBarActions,
  BulkConvertDownloadButton,
  BulkConvertDismissButton,
  PdlBulkCsvDownloadIcon,
} from '../Container/styled'

/**
 * Bulk import banner on Ready to Review: poll Pear for in-progress work (by id or any),
 * then summary + CSV download + dismiss.
 */
const BulkConvertStatusBar = ({
  showInitially = false,
  bulkConversionId = null,
  conversion = null,
  csvReadyForDownload = false,
  tests = [],
  dismissed = false,
  onComplete,
  onDismiss,
  onIncompleteCountChange,
}) => {
  const [incompleteCount, setIncompleteCount] = useState(null)
  const [csvBusy, setCsvBusy] = useState(false)
  const [fetchedConversion, setFetchedConversion] = useState(null)
  const [resolvingComplete, setResolvingComplete] = useState(false)

  const hasScopedId =
    bulkConversionId != null && String(bulkConversionId).trim() !== ''

  const conversionData = conversion || fetchedConversion

  const isConversionSettled = (c) => {
    if (!c) return false
    const status = String(c.status || '').toLowerCase()
    return status && !['queued', 'processing'].includes(status)
  }

  React.useEffect(() => {
    let cancelled = false
    let timeoutId = null
    let delay = INCOMPLETE_CONVERSIONS_POLL_INITIAL_MS

    setIncompleteCount(null)
    setFetchedConversion(null)
    setResolvingComplete(false)

    const conversionIds = hasScopedId
      ? [String(bulkConversionId).trim()]
      : undefined

    const resolveComplete = async () => {
      if (!hasScopedId) {
        onComplete?.(null)
        return
      }
      setResolvingComplete(true)
      const c = await fetchConversionByIdPear(bulkConversionId)
      if (cancelled) return
      setFetchedConversion(c)
      setResolvingComplete(false)
      onComplete?.(c)
    }

    const poll = async () => {
      const { count } = await fetchIncompleteConversionsCount({ conversionIds })
      if (cancelled) return

      setIncompleteCount(count)
      onIncompleteCountChange?.(count)
      if (count === 0) {
        await resolveComplete()
        return
      }

      delay = Math.min(delay * 2, INCOMPLETE_CONVERSIONS_POLL_MAX_MS)
      timeoutId = setTimeout(poll, delay)
    }

    poll()

    return () => {
      cancelled = true
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [
    showInitially,
    bulkConversionId,
    hasScopedId,
    onComplete,
    onIncompleteCountChange,
  ])

  const showComplete =
    !dismissed &&
    Boolean(
      bulkConversionId &&
        conversionData &&
        (csvReadyForDownload ||
          isConversionSettled(conversionData) ||
          incompleteCount === 0)
    )

  const showInProgress =
    !showComplete &&
    !dismissed &&
    (resolvingComplete ||
      (showInitially && incompleteCount === null) ||
      (hasScopedId && incompleteCount === null) ||
      (incompleteCount !== null && incompleteCount > 0))

  const handleCsvDownload = useCallback(() => {
    if (!showComplete || csvBusy || !conversionData) return
    setCsvBusy(true)
    try {
      const csv = buildBulkConversionCsv(conversionData, tests)
      const safeId =
        conversionData?.conversion_id ?? String(bulkConversionId || 'export')
      const stamp = conversionData?.created_at
      downloadCsvFile(
        stamp
          ? `bulk-conversion-${stamp}-${safeId}.csv`
          : `bulk-conversion-${safeId}.csv`,
        csv
      )
      segmentApi.genericEventTrack(
        BULK_CONVERTER_SEGMENT.MY_CONTENT_BULK_CONVERSION_CSV_DOWNLOAD,
        {
          bulkConversionId,
          fileCount:
            conversionData?.files?.length ?? conversionData?.total_files ?? 0,
        }
      )
      // Persist dismiss for refresh / return visits only; keep banner visible on this page.
      if (bulkConversionId != null) {
        markBulkConversionBannerDismissed(bulkConversionId)
      }
    } finally {
      setCsvBusy(false)
    }
  }, [showComplete, csvBusy, conversionData, tests, bulkConversionId])

  if (!showInProgress && !showComplete) return null

  if (showComplete) {
    const { successCount, totalCount } = getBulkConversionFileSummary(
      conversionData
    )
    const total = Math.max(0, Number(totalCount) || 0)
    const success = Math.max(0, Math.min(Number(successCount) || 0, total))
    const fileLabel = total === 1 ? 'File' : 'Files'
    const summary =
      total > 0
        ? `${success}/${total} ${fileLabel} uploaded successfully.`
        : 'Files uploaded successfully.'

    return (
      <BulkConvertStatusBarWrapper
        role="status"
        aria-live="polite"
        data-cy="bulkConvertStatusBarComplete"
      >
        <BulkConvertStatusBarPanel>
          <BulkConvertStatusBarSummaryRow>
            <BulkConvertStatusIconWrap aria-hidden>
              <IconCheck color={white} width={12} height={12} />
            </BulkConvertStatusIconWrap>
            <BulkConvertStatusMessage>{summary}</BulkConvertStatusMessage>
          </BulkConvertStatusBarSummaryRow>
          <BulkConvertStatusBarActions>
            <BulkConvertDownloadButton
              type="button"
              data-cy="pdlBulkDownloadCsv"
              aria-label="Download files for this import"
              disabled={csvBusy}
              onClick={handleCsvDownload}
            >
              <PdlBulkCsvDownloadIcon aria-hidden />
              Download Summary
            </BulkConvertDownloadButton>
            <BulkConvertDismissButton
              type="button"
              aria-label="Dismiss upload summary"
              data-cy="bulkConvertStatusBarDismiss"
              onClick={onDismiss}
            >
              <IconClose width={12} height={12} />
            </BulkConvertDismissButton>
          </BulkConvertStatusBarActions>
        </BulkConvertStatusBarPanel>
      </BulkConvertStatusBarWrapper>
    )
  }

  return (
    <BulkConvertStatusBarWrapper role="status" aria-live="polite">
      <Alert
        type="info"
        message={
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 16,
            }}
          >
            <IconHourGlass width={24} height={24} />
            Creating test questions from your documents. This may take a few
            minutes.
          </div>
        }
      />
    </BulkConvertStatusBarWrapper>
  )
}

export default BulkConvertStatusBar
